import React from "react";
import { Link, NavLink } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Camera, FolderKanban, Image as ImageIcon, BarChart3, Receipt, Settings as SettingsIcon, Compass, Shield, LogOut, Menu, X, Users, Package, Inbox, BookOpen, Film, ShoppingBag, Sparkles, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import NavArrows from "@/components/NavArrows";
import Logo from "@/components/Logo";
import TrialBanner from "@/components/TrialBanner";

const NAV_GROUPS = [
  {
    label: "Studio",
    items: [
      { to: "/dashboard", label: "Dashboard", icon: Camera },
      { to: "/projects", label: "Projects", icon: FolderKanban },
      { to: "/leads", label: "Leads", icon: Inbox },
      { to: "/analytics", label: "Analytics", icon: BarChart3 },
      { to: "/invoices", label: "Invoices", icon: Receipt },
    ],
  },
  {
    label: "Deliver",
    items: [
      { to: "/galleries", label: "Galleries", icon: ImageIcon },
      { to: "/albums", label: "Albums", icon: BookOpen },
      { to: "/prints", label: "Prints", icon: Package },
      { to: "/videos", label: "Videos", icon: Film },
    ],
  },
  {
    label: "Grow",
    items: [
      { to: "/showcase", label: "Showcase", icon: Users },
      { to: "/shop", label: "Shop", icon: ShoppingBag },
      { to: "/marketing", label: "Marketing", icon: Sparkles },
      { to: "/discover", label: "Discover", icon: Compass },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/settings", label: "Settings", icon: SettingsIcon },
    ],
  },
];

const COLLAPSED_KEY = "pb.sidebar.collapsed";
const AUTO_COLLAPSE_BREAKPOINT = 1200;   // px — below this the sidebar auto-collapses on desktop

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = React.useState(false);              // mobile slide-in

  const initialCollapsed = React.useMemo(() => {
    if (typeof window === "undefined") return false;
    // Prefer the persisted choice if there is one — this respects a user's
    // explicit decision across sessions.
    const saved = window.localStorage.getItem(COLLAPSED_KEY);
    if (saved === "1") return true;
    if (saved === "0") return false;
    // No saved choice → auto-collapse on narrow desktops so tablets in
    // landscape (typically ~1024-1180px) don't waste 25% of the viewport on
    // a nav column.
    return window.innerWidth < AUTO_COLLAPSE_BREAKPOINT;
  }, []);
  const [collapsed, setCollapsed] = React.useState(initialCollapsed);

  const persistCollapsed = React.useCallback((next) => {
    try { window.localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0"); } catch {}
  }, []);

  const toggleCollapsed = React.useCallback(() => {
    setCollapsed(v => { const next = !v; persistCollapsed(next); return next; });
  }, [persistCollapsed]);

  // Auto-collapse on viewport shrink so tablet landscape ↔ full desktop
  // stays comfortable. We only auto-toggle when the user hasn't set an
  // explicit preference in this browser.
  React.useEffect(() => {
    const onResize = () => {
      const saved = window.localStorage.getItem(COLLAPSED_KEY);
      if (saved === "1" || saved === "0") return;    // respect explicit choice
      const shouldCollapse = window.innerWidth < AUTO_COLLAPSE_BREAKPOINT;
      setCollapsed(prev => prev === shouldCollapse ? prev : shouldCollapse);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Keyboard shortcuts — [ collapses, ] expands, \ toggles. Skips when a
  // form field has focus so typing brackets into a text input doesn't hijack.
  React.useEffect(() => {
    const isEditable = (el) => {
      if (!el) return false;
      const tag = (el.tagName || "").toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
    };
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditable(document.activeElement)) return;
      if (e.key === "[") { setCollapsed(true); persistCollapsed(true); e.preventDefault(); }
      else if (e.key === "]") { setCollapsed(false); persistCollapsed(false); e.preventDefault(); }
      else if (e.key === "\\") { toggleCollapsed(); e.preventDefault(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [toggleCollapsed, persistCollapsed]);

  const asideWidth = collapsed ? "lg:w-16" : "lg:w-64";

  return (
    <div className="min-h-screen flex bg-stone-950">
      {/* Backdrop for mobile drawer */}
      {open && (
        <button
          aria-label="Close menu"
          data-testid="sidebar-backdrop"
          onClick={()=>setOpen(false)}
          className="lg:hidden fixed inset-0 bg-stone-950/60 backdrop-blur-sm z-30"
        />
      )}
      {/* Sidebar */}
      <aside
        data-testid="sidebar"
        data-collapsed={collapsed ? "1" : "0"}
        className={`fixed lg:static inset-y-0 left-0 z-40 w-64 ${asideWidth} border-r border-white/5 bg-stone-950/80 backdrop-blur transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 transition-[transform,width] duration-200`}
      >
        <div className={`p-6 border-b border-white/5 flex items-center ${collapsed ? "lg:justify-center lg:p-3" : "justify-between"}`}>
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
            {collapsed ? <Logo size={28} /> : <Logo size={32} withWordmark />}
          </Link>
          {/* Desktop collapse toggle — hidden on mobile (which uses the top-bar hamburger). */}
          <button
            type="button"
            onClick={toggleCollapsed}
            data-testid="btn-sidebar-collapse"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className={`hidden lg:inline-flex p-1.5 rounded text-stone-500 hover:text-stone-100 hover:bg-white/5 transition ${collapsed ? "lg:hidden" : ""}`}
          >
            <PanelLeftClose className="w-4 h-4"/>
          </button>
        </div>

        {/* When collapsed, put the "expand" chevron below the mini-logo so it stays reachable. */}
        {collapsed && (
          <div className="hidden lg:flex justify-center py-2 border-b border-white/5">
            <button
              type="button"
              onClick={toggleCollapsed}
              data-testid="btn-sidebar-expand"
              aria-label="Expand sidebar"
              title="Expand sidebar"
              className="p-1.5 rounded text-stone-400 hover:text-stone-100 hover:bg-white/5"
            ><PanelLeftOpen className="w-4 h-4"/></button>
          </div>
        )}

        <nav className={`p-4 space-y-4 overflow-y-auto ${collapsed ? "lg:p-2 lg:space-y-2" : ""}`} style={{ maxHeight: "calc(100vh - 220px)" }}>
          {NAV_GROUPS.map((grp) => (
            <div key={grp.label}>
              {!collapsed && (
                <div
                  className="sticky top-0 z-10 px-3 pt-1 pb-1 bg-stone-950/85 backdrop-blur-sm text-[10px] uppercase tracking-widest text-stone-500"
                  data-testid={`nav-group-${grp.label.toLowerCase()}`}
                >{grp.label}</div>
              )}
              <div className="space-y-1 mt-1">
                {grp.items.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={`nav-${label.toLowerCase()}`}
                    onClick={()=>setOpen(false)}
                    title={collapsed ? label : undefined}
                    className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${collapsed ? "lg:justify-center lg:px-2" : ""} ${isActive ? "bg-amber-500/10 text-amber-400" : "text-stone-400 hover:bg-white/5 hover:text-stone-100"}`}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className={collapsed ? "lg:hidden" : ""}>{label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
          {(user?.role === "admin" || user?.is_owner) && (
            <div>
              {!collapsed && <div className="sticky top-0 z-10 px-3 pt-1 pb-1 bg-stone-950/85 backdrop-blur-sm text-[10px] uppercase tracking-widest text-amber-400/70">Admin</div>}
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                onClick={()=>setOpen(false)}
                title={collapsed ? "Admin" : undefined}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-md text-sm mt-1 ${collapsed ? "lg:justify-center lg:px-2" : ""} ${isActive ? "bg-amber-500/10 text-amber-400" : "text-stone-400 hover:bg-white/5 hover:text-stone-100"}`}
              >
                <Shield className="w-4 h-4 shrink-0" />
                <span className={collapsed ? "lg:hidden" : ""}>Admin</span>
              </NavLink>
            </div>
          )}
        </nav>

        <div className={`absolute bottom-0 left-0 right-0 p-4 border-t border-white/5 ${collapsed ? "lg:p-2" : ""}`}>
          <div className={`flex items-center gap-3 mb-3 ${collapsed ? "lg:mb-2 lg:justify-center" : ""}`}>
            {user?.picture ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full shrink-0" /> : <div className="w-9 h-9 rounded-full bg-stone-800 shrink-0" />}
            <div className={`flex-1 min-w-0 ${collapsed ? "lg:hidden" : ""}`}>
              <p className="text-sm truncate">{user?.display_name}</p>
              {user?.role === "admin" ? (
                <span data-testid="admin-plan-badge" className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-amber-500/15 border border-amber-500/40 text-amber-300">
                  <Shield className="w-3 h-3"/>Admin · Studio
                </span>
              ) : (
                <p className="text-xs text-stone-500 capitalize">{user?.plan} plan</p>
              )}
            </div>
          </div>
          {collapsed ? (
            <button
              onClick={logout}
              title="Sign out"
              data-testid="btn-logout"
              className="hidden lg:flex w-full items-center justify-center p-2 rounded-md text-stone-400 hover:text-stone-100 hover:bg-white/5"
            ><LogOut className="w-4 h-4"/></button>
          ) : null}
          {!collapsed && (
            <Button data-testid="btn-logout" variant="outline" size="sm" onClick={logout} className="w-full">
              <LogOut className="w-3.5 h-3.5 mr-2" /> Sign out
            </Button>
          )}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="lg:hidden glass sticky top-0 z-30 px-4 py-3 flex items-center justify-between">
          <button onClick={() => setOpen(!open)} data-testid="btn-mobile-menu" className="p-2" aria-label={open ? "Close menu" : "Open menu"}>
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <span className="font-display text-lg flex items-center gap-2"><Logo size={22}/>Photo Bridge</span>
          <NavArrows />
        </header>
        <div className="hidden lg:flex items-center gap-3 px-8 lg:px-12 pt-8 pb-2">
          <NavArrows />
          <span className="text-xs text-stone-600 uppercase tracking-widest">History</span>
        </div>
        <main className="p-4 md:p-8 lg:px-12 lg:pt-4 lg:pb-12 max-w-7xl">
          <TrialBanner />
          {children}
        </main>
      </div>
    </div>
  );
}
